import { useState, useCallback } from 'react';

/**
 * Custom hook to manage body region overlay state.
 *
 * NOTE: Overlays are now ALWAYS ON by default. Toggle controls have been removed.
 * This hook only manages opacity which can be adjusted by the user.
 *
 * Manages:
 * - Overlay opacity (user-adjustable)
 * - All other preferences are forced to TRUE (showOverlay, showHulls, showLabels)
 */

interface OverlayPreferences {
  showOverlay: boolean;
  showHulls: boolean;
  showLabels: boolean;
  opacity: number;
}

const DEFAULT_PREFERENCES: OverlayPreferences = {
  showOverlay: true,  // FORCED ON
  showHulls: true,     // FORCED ON
  showLabels: true,    // FORCED ON
  opacity: 0.5,
};

function useBodyRegionOverlay() {
  // Only opacity is mutable - all other preferences are forced to defaults
  const [opacity, setOpacity] = useState<number>(DEFAULT_PREFERENCES.opacity);

  // Update opacity (0-1)
  const updateOpacity = useCallback((newOpacity: number) => {
    const clampedOpacity = Math.max(0, Math.min(1, newOpacity));
    setOpacity(clampedOpacity);
  }, []);

  // Legacy compatibility - these always return true
  const showOverlay = true;
  const showHulls = true;
  const showLabels = true;

  // Legacy compatibility - these are no-ops but kept for API compatibility
  const toggleOverlay = useCallback(() => {
    console.warn('[useBodyRegionOverlay] toggleOverlay is deprecated - overlays are always on');
  }, []);

  const updateShowOverlay = useCallback(() => {
    console.warn('[useBodyRegionOverlay] updateShowOverlay is deprecated - overlays are always on');
  }, []);

  const updateShowHulls = useCallback(() => {
    console.warn('[useBodyRegionOverlay] updateShowHulls is deprecated - hulls are always on');
  }, []);

  const updateShowLabels = useCallback(() => {
    console.warn('[useBodyRegionOverlay] updateShowLabels is deprecated - labels are always on');
  }, []);

  const resetToDefaults = useCallback(() => {
    setOpacity(DEFAULT_PREFERENCES.opacity);
  }, []);

  return {
    showOverlay,
    showHulls,
    showLabels,
    opacity,
    toggleOverlay,
    updateShowOverlay,
    updateShowHulls,
    updateShowLabels,
    updateOpacity,
    resetToDefaults,
  };
}

export default useBodyRegionOverlay;
